import { defineMetrics } from "../types.js"

/**
 * - `Project` is a **saved filter** (products + countries) and holds no tasks at all;
 * `lib/Project/GetMapping.js` computes its contents by matching products, so "how many tasks
 * per project" has no foreign key to join on in the first place.
 * - `UserTask` references tracking / checklist / application / ccpTask, **but none of them is
 * `Project`**, and its status enum has only two values, `todo | completed`.
 * - `CCPTask.projectId` is `required: true` and points at `CCPProject`, and its status is
 * exactly the five-stage enum the stacked bar chart wants.
 * ⚠ The association is read from **`CCPTask.projectId`, not `CCPProject.tasks`**. Both exist and
 * they can disagree; the former is required on write, so it is the one that is never missing.
 */
export const projectsMetrics = defineMetrics([
  {
    id: "total_projects",
    name: "Total Projects",
    description: "Total number of CCP Projects.",
    logic: "Count Project records.",
    source: "ccpProject.countDocuments()",
    domain: "projects",
    srvModule: "projects",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["line", "bar"],
    coveredByV1: false,
    xlsxRow: 61,
  },
  {
    id: "total_tasks",
    name: "Total Tasks",
    description: "Total number of Tasks across all Projects.",
    logic: "Count Task records.",
    source: "ccpTask.countDocuments() — CCP tasks, not UserTask",
    domain: "projects",
    srvModule: "projects",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["line", "bar"],
    coveredByV1: false,
    xlsxRow: 62,
  },
  {
    id: "tasks_per_project",
    name: "Tasks per Project",
    description: "Number of Tasks associated with each Project.",
    logic: "Group Tasks by Project and count.",
    source: "ccpTask.projectId, named via a lookup into ccpProject",
    domain: "projects",
    srvModule: "projects",
    priority: "must",
    valueType: "count",
    dims: ["entity"],
    rollup: "last",
    defaultWidget: "histogram",
    altWidgets: ["hbar", "table", "bar"],
    coveredByV1: false,
    xlsxRow: 63,
  },
  {
    id: "task_status",
    name: "Task Status",
    description: "Current status of a Task.",
    logic: "Retrieve Task status field.",
    source: "ccpTask.status, the five-stage assessment enum",
    domain: "projects",
    srvModule: "projects",
    priority: "must",
    valueType: "count",
    dims: ["class"],
    rollup: "last",
    defaultWidget: "stacked-bar",
    altWidgets: ["pie", "hbar", "table"],
    coveredByV1: false,
    xlsxRow: 64,
  },
  {
    id: "project_completion_progress",
    name: "Project Completion Progress",
    description: "Percentage of completed Tasks within a Project.",
    logic: "(Completed Tasks ÷ Total Tasks) × 100.",
    source: "ccpTask(status=completed) / ccpTask, per projectId",
    domain: "projects",
    srvModule: "projects",
    priority: "must",
    valueType: "ratio",
    dims: ["entity"],
    rollup: "avg",
    defaultWidget: "gauge",
    altWidgets: ["hbar", "table"],
    coveredByV1: false,
    caveat:
      "Emitted as a 0..1 ratio; the dictionary's multiplication by 100 is a presentation choice the gauge applies. A project with no tasks emits nothing rather than 0, because 0/0 has no completion percentage.",
    xlsxRow: 65,
  },
])
